
import React, { useState, useRef, useCallback } from 'react';
import { UploadCloud, FileText, CheckCircle, AlertCircle, Loader2, X, Plus, Crop as CropIcon, MousePointerClick, Trash2, Check, ArrowLeft, ArrowRight, Layers, Microscope, LayoutGrid, Sparkles, TrendingUp, BookOpen, BrainCircuit } from 'lucide-react';
import { useAppStore } from '../store/useAppStore';
import { Button } from '../components/ui/Button';
import { useNavigate, Link } from 'react-router-dom';
import { CropModal, TextItem } from '../components/ui/CropModal';
import * as pdfjsLib from 'pdfjs-dist';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../lib/utils';

const pdfjs: any = pdfjsLib;
const PDFJS_VERSION = '5.4.449';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;

interface PageThumbnail {
  pageNum: number;
  thumbnailUrl: string;
}

const UploadPage = () => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [renderProgress, setRenderProgress] = useState(0);
  const [pageThumbnails, setPageThumbnails] = useState<PageThumbnail[]>([]);
  const [currentPdfId, setCurrentPdfId] = useState<string | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const pdfDocRef = useRef<any>(null);

  const [cropModal, setCropModal] = useState<{
    isOpen: boolean;
    imageUrl: string;
    pageNum: number;
    textItems: TextItem[];
    isLoadingPage: boolean;
  }>({ 
    isOpen: false, 
    imageUrl: '', 
    pageNum: 0,
    textItems: [],
    isLoadingPage: false
  });

  const { createPdfRecord, addMultipleProcessedImages, getPdfImages } = useAppStore();
  const navigate = useNavigate();

  const savedCount = currentPdfId ? getPdfImages(currentPdfId).length : 0;

  const loadPdfThumbnails = useCallback(async (selectedFile: File) => {
    setIsProcessing(true);
    setRenderProgress(0);
    setPageThumbnails([]);
    setError(null);
    
    try {
        const pdfRecord = await createPdfRecord(selectedFile);
        setCurrentPdfId(pdfRecord.id);

        const arrayBuffer = await selectedFile.arrayBuffer();
        const loadingTask = pdfjs.getDocument({ 
          data: arrayBuffer,
          useSystemFonts: true,
          isEvalSupported: false 
        });
        
        const pdf = await loadingTask.promise;
        pdfDocRef.current = pdf;
        
        const totalPages = pdf.numPages;
        const limit = Math.min(totalPages, 50);

        for (let i = 1; i <= limit; i++) {
            try {
                const page = await pdf.getPage(i);
                const viewport = page.getViewport({ scale: 0.25 }); 
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d', { alpha: false });
                if (!ctx) continue;
                
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                
                await page.render({ 
                  canvasContext: ctx, 
                  viewport,
                  intent: 'display'
                }).promise;
                
                const thumb = canvas.toDataURL('image/jpeg', 0.6);
                setPageThumbnails(prev => [...prev, { pageNum: i, thumbnailUrl: thumb }]);
                setRenderProgress(Math.round((i / limit) * 100));
                await new Promise(resolve => setTimeout(resolve, 10));
            } catch (pageError) {
                console.warn(`Skipping page ${i} due to render error`, pageError);
            }
        }
    } catch (e: any) {
        setError(`Processing failed: ${e.message || "Unknown error"}. Check if the file is valid.`);
        setFile(null);
    } finally {
        setIsProcessing(false);
    }
  }, [createPdfRecord]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        setError('Please select a valid PDF file.');
        setFile(null);
        return;
      }
      setError(null);
      setFile(selectedFile);
      loadPdfThumbnails(selectedFile);
    }
  };

  const deletePage = (pageNum: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setPageThumbnails(prev => prev.filter(p => p.pageNum !== pageNum));
  };

  const loadPageData = async (pageNum: number) => {
    if (!pdfDocRef.current) return null;
    try {
        const page = await pdfDocRef.current.getPage(pageNum);
        const scale = 2.0;
        const viewport = page.getViewport({ scale }); 
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if(!ctx) return null;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: ctx, viewport }).promise;
        
        const textContent = await page.getTextContent();
        const textItems: TextItem[] = textContent.items.map((item: any) => {
            const pdfX = item.transform[4];
            const pdfY = item.transform[5];
            const pdfH = item.height || 10; 
            const pdfW = item.width || (item.str.length * 5); 
            const [x, y] = viewport.convertToViewportPoint(pdfX, pdfY);
            return { text: item.str, x, y: y - (pdfH * scale), width: pdfW * scale, height: pdfH * scale };
        });
        
        return { imageUrl: canvas.toDataURL('image/jpeg', 0.9), textItems };
    } catch (e) {
        console.error("Failed to load detail page:", e);
        return null;
    }
  };

  const openPageForCropping = async (pageNum: number) => {
      setCropModal(prev => ({ ...prev, isOpen: true, pageNum, isLoadingPage: true }));
      const data = await loadPageData(pageNum);
      if (data) {
          setCropModal({ isOpen: true, imageUrl: data.imageUrl, pageNum, textItems: data.textItems, isLoadingPage: false });
      } else {
          setCropModal(prev => ({ ...prev, isOpen: false, isLoadingPage: false }));
          setError("Failed to open page for detailed review.");
      }
  };

  const navigatePageInModal = async (direction: 'next' | 'prev') => {
      const currentIndex = pageThumbnails.findIndex(p => p.pageNum === cropModal.pageNum);
      if (currentIndex === -1) return;
      const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
      if (newIndex >= 0 && newIndex < pageThumbnails.length) {
          const newPageNum = pageThumbnails[newIndex].pageNum;
          setCropModal(prev => ({ ...prev, isLoadingPage: true }));
          const data = await loadPageData(newPageNum);
          if (data) {
              setCropModal(prev => ({ ...prev, imageUrl: data.imageUrl, pageNum: newPageNum, textItems: data.textItems, isLoadingPage: false }));
          } else {
              setCropModal(prev => ({ ...prev, isLoadingPage: false }));
          }
      }
  };

  const handleSaveCrop = async (crop: { imageUrl: string, label: string }) => {
      if (!currentPdfId || !file) return;
      const payload = [{
          pdfId: currentPdfId,
          imageBase64: crop.imageUrl,
          originalName: `${file.name} - Page ${cropModal.pageNum}`,
          label: crop.label
      }];

      try {
        await addMultipleProcessedImages(payload);
      } catch (err) {
        setError("Failed to save extracted image to your library.");
        throw err;
      }
  };

  const handleFinish = () => {
    setShowSummary(true);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-40">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 py-4">
        <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight uppercase">Extract Slides</h1>
            <p className="text-slate-500 mt-1 text-sm font-medium">Capture high-resolution histology slides from your source pages.</p>
        </div>
        
        <AnimatePresence>
          {savedCount > 0 && (
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-xl border border-emerald-100 shadow-sm"
            >
              <CheckCircle className="h-4 w-4" />
              <span className="text-xs font-black uppercase tracking-wider">{savedCount} Collected</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {!file && (
        <div className="bg-white p-4 sm:p-8 rounded-[2rem] border border-slate-200 shadow-sm">
            <div className="border-2 border-dashed border-slate-200 rounded-[1.5rem] p-8 sm:p-16 flex flex-col items-center justify-center hover:bg-slate-50 active:bg-slate-100/80 transition-all cursor-pointer relative group min-h-[300px] sm:min-h-[400px]">
                <input type="file" accept=".pdf" onChange={handleFileChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                <div className="bg-primary-50 p-6 sm:p-8 rounded-[2rem] mb-6 group-hover:scale-110 group-active:scale-95 transition-transform"><UploadCloud className="h-10 w-10 sm:h-12 sm:w-12 text-primary-500" /></div>
                <h3 className="text-xl sm:text-2xl font-black text-slate-900 mb-2 text-center uppercase tracking-tight">Upload Source</h3>
                <p className="text-slate-500 text-center max-w-sm text-sm font-medium leading-relaxed px-4">Tap to browse or drag your histology PDF here to begin extraction.</p>
                <div className="mt-8 px-6 py-2 bg-slate-100 rounded-full text-[10px] font-black uppercase tracking-widest text-slate-400">PDF Document Only</div>
            </div>
            
            <div className="mt-8 flex justify-center">
              <Link to="/library">
                <Button variant="ghost" className="text-slate-400 font-bold uppercase tracking-widest text-[10px] hover:text-slate-900">
                  <ArrowLeft className="h-3 w-3 mr-2" /> Return to Library
                </Button>
              </Link>
            </div>
        </div>
      )}

      {file && (
        <div className="space-y-6">
            <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4 overflow-hidden">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <div className="h-12 w-12 bg-red-50 rounded-2xl flex items-center justify-center border border-red-100 flex-shrink-0"><FileText className="h-6 w-6 text-red-500" /></div>
                    <div className="min-w-0 flex-1">
                        <p className="font-black text-slate-900 text-sm truncate uppercase tracking-tight">{file.name}</p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                          {pageThumbnails.length} {isProcessing ? 'Rendering...' : 'Pages Loaded'}
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
                  <Button variant="ghost" size="sm" onClick={() => navigate('/library')} className="text-slate-400 hover:text-slate-900 text-[10px] uppercase font-bold tracking-wider h-11 sm:h-9">
                    Discard
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setFile(null); setPageThumbnails([]); }} className="rounded-xl text-[10px] uppercase font-bold tracking-wider h-11 sm:h-9">
                    New File
                  </Button>
                </div>
            </div>

            {isProcessing && pageThumbnails.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 bg-white rounded-[2rem] border border-slate-200 shadow-sm">
                    <Loader2 className="h-12 w-12 text-primary-500 animate-spin mb-6" />
                    <p className="text-slate-400 font-black text-[10px] uppercase tracking-[0.4em]">Optimizing Engine...</p>
                </div>
            ) : (
                <div className="space-y-8">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6">
                        <AnimatePresence>
                          {pageThumbnails.map((page) => (
                              <motion.div 
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  key={page.pageNum}
                                  onClick={() => openPageForCropping(page.pageNum)}
                                  className="group relative aspect-[3/4] bg-white rounded-2xl overflow-hidden cursor-pointer border-2 border-transparent hover:border-primary-500 transition-all shadow-sm ring-1 ring-slate-200 active:scale-95"
                              >
                                  <img src={page.thumbnailUrl} alt={`Page ${page.pageNum}`} className="w-full h-full object-contain p-2" />
                                  <div className="absolute bottom-2 left-2 bg-slate-900/80 text-white text-[8px] font-black px-1.5 py-0.5 rounded-md backdrop-blur-md uppercase tracking-widest">Pg {page.pageNum}</div>
                                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-primary-500/10">
                                      <div className="bg-white p-3 rounded-2xl shadow-2xl scale-75 group-hover:scale-100 transition-transform duration-300">
                                        <CropIcon className="h-6 w-6 text-primary-600" />
                                      </div>
                                  </div>
                                  <button 
                                    onClick={(e) => deletePage(page.pageNum, e)} 
                                    className="absolute top-2 right-2 p-2 bg-white/90 rounded-xl text-slate-400 hover:text-red-500 sm:opacity-0 group-hover:opacity-100 transition-all hover:shadow-lg"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                              </motion.div>
                          ))}
                          
                          {isProcessing && (
                            <div className="aspect-[3/4] bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center p-4">
                               <Loader2 className="h-6 w-6 text-primary-400 animate-spin mb-2" />
                               <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest text-center">Rendering... {renderProgress}%</p>
                            </div>
                          )}
                        </AnimatePresence>
                    </div>
                </div>
            )}
            
            <AnimatePresence>
              {savedCount > 0 && (
                <motion.div 
                  initial={{ y: 100, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 100, opacity: 0 }}
                  className="fixed bottom-8 left-0 right-0 px-6 flex justify-center z-[60]"
                >
                    <Button 
                      className="w-full max-w-sm shadow-2xl h-14 sm:h-16 text-xs font-black uppercase tracking-[0.2em] rounded-full bg-primary-600 text-white border border-white/20 active:scale-95 transition-all" 
                      onClick={handleFinish}
                    >
                        <CheckCircle className="mr-3 h-5 w-5 text-white" /> 
                        Finish & Start Practice
                    </Button>
                </motion.div>
              )}
            </AnimatePresence>
        </div>
      )}

      {/* REFINED COMPLETION MODAL - CLEAN CLINICAL LIGHT THEME */}
      <AnimatePresence>
        {showSummary && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
          >
            <motion.div 
              initial={{ scale: 0.95, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 20, opacity: 0 }}
              className="bg-white w-full max-w-lg rounded-[2.5rem] p-8 sm:p-12 text-center shadow-[0_30px_60px_-12px_rgba(0,0,0,0.2)] border border-slate-100 relative"
            >
              <button 
                onClick={() => setShowSummary(false)}
                className="absolute top-8 right-8 p-3 bg-slate-50 hover:bg-slate-100 rounded-2xl transition-all group"
              >
                <X className="h-5 w-5 text-slate-400 group-hover:text-slate-900" />
              </button>

              <div className="bg-emerald-50 w-16 h-16 rounded-[1.5rem] flex items-center justify-center mx-auto mb-8 border border-emerald-100">
                 <CheckCircle className="h-8 w-8 text-emerald-500" />
              </div>

              <div className="space-y-2 mb-10">
                <h2 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Ready to Review</h2>
                <p className="text-slate-500 font-medium text-sm">
                  You successfully extracted <span className="text-emerald-600 font-bold">{savedCount} histology slides</span>. How would you like to start your session?
                </p>
              </div>

              <div className="space-y-3">
                <button 
                  onClick={() => navigate(`/practice/${currentPdfId}`)}
                  className="w-full flex items-center justify-between p-5 bg-primary-600 text-white rounded-2xl hover:bg-primary-700 transition-colors shadow-lg shadow-primary-600/20"
                >
                  <div className="flex items-center gap-4">
                    <div className="bg-white/10 p-2.5 rounded-xl"><BrainCircuit className="h-6 w-6" /></div>
                    <div className="text-left">
                      <p className="text-sm font-black uppercase tracking-widest">Active Recall Quiz</p>
                      <p className="text-[10px] text-white/70 font-medium">Test your identification skills</p>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5" />
                </button>

                <div className="grid grid-cols-2 gap-3">
                  <button 
                    onClick={() => navigate(`/flashcards/${currentPdfId}`)}
                    className="flex flex-col items-center p-5 bg-white border border-slate-200 rounded-2xl hover:border-primary-200 hover:bg-primary-50/20 transition-all"
                  >
                    <Layers className="h-6 w-6 text-primary-500 mb-2" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-900">Study Cards</span>
                  </button>

                  <button 
                    onClick={() => navigate('/library')}
                    className="flex flex-col items-center p-5 bg-white border border-slate-200 rounded-2xl hover:border-slate-300 hover:bg-slate-50 transition-all"
                  >
                    <LayoutGrid className="h-6 w-6 text-slate-400 mb-2" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-900">Library</span>
                  </button>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-50">
                 <button onClick={() => setShowSummary(false)} className="text-[10px] font-black text-slate-400 hover:text-primary-600 uppercase tracking-widest transition-colors">
                   Continue adding more slides
                 </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
            <div className="fixed bottom-24 sm:bottom-28 left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 px-6 py-4 bg-red-600 text-white rounded-2xl shadow-2xl flex items-center justify-between z-50 sm:w-auto sm:min-w-[400px]">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
                  <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest leading-tight">{error}</span>
                </div>
                <button onClick={() => setError(null)} className="ml-4 p-2 hover:bg-white/20 rounded-xl"><X className="h-4 w-4" /></button>
            </div>
      )}

      <CropModal 
          isOpen={cropModal.isOpen}
          onClose={() => setCropModal({ ...cropModal, isOpen: false })}
          imageUrl={cropModal.imageUrl}
          pageTextItems={cropModal.textItems}
          onSave={handleSaveCrop}
          currentPageNum={cropModal.pageNum}
          onNextPage={() => navigatePageInModal('next')}
          onPrevPage={() => navigatePageInModal('prev')}
          hasNext={pageThumbnails.findIndex(p => p.pageNum === cropModal.pageNum) < pageThumbnails.length - 1}
          hasPrev={pageThumbnails.findIndex(p => p.pageNum === cropModal.pageNum) > 0}
          isLoadingPage={cropModal.isLoadingPage}
      />
    </div>
  );
};

export default UploadPage;
